import { distanceUnits, type DistanceUnit } from "earthbrain-viewer";

export { distanceUnits };
export type { DistanceUnit };

export const areaUnits = {
	SQUARE_METERS: "SQUARE_METERS",
	SQUARE_FEET: "SQUARE_FEET",
	ACRES: "ACRES",
	HECTARES: "HECTARES",
} as const;

export type AreaUnit = (typeof areaUnits)[keyof typeof areaUnits];

export const volumeUnits = {
	CUBIC_METERS: "CUBIC_METERS",
	CUBIC_YARDS: "CUBIC_YARDS",
} as const;

export type VolumeUnit = (typeof volumeUnits)[keyof typeof volumeUnits];

export interface SiteUnitSettings {
	distance: DistanceUnit;
	area: AreaUnit;
	volume: VolumeUnit;
}

/** Default site unit settings (metric) */
export const DEFAULT_SITE_UNITS = {
	distance: distanceUnits.METERS,
	area: areaUnits.SQUARE_METERS,
	volume: volumeUnits.CUBIC_METERS,
} satisfies SiteUnitSettings;

/** Landlog Platform API `/v2/sites/:id` unit.distance value -> internal constant. */
export const DistanceUnitsMap = {
	m: distanceUnits.METERS,
	ft: distanceUnits.FEET,
	ftUS: distanceUnits.US_SURVEY_FEET,
} as const;

/** Landlog Platform API `/v2/sites/:id` unit.area value -> internal constant. */
export const AreaUnitsMap = {
	m2: areaUnits.SQUARE_METERS,
	ft2: areaUnits.SQUARE_FEET,
	ac: areaUnits.ACRES,
	ha: areaUnits.HECTARES,
} as const;

/** Landlog Platform API `/v2/sites/:id` unit.volume value -> internal constant. */
export const VolumeUnitsMap = {
	m3: volumeUnits.CUBIC_METERS,
	yd3: volumeUnits.CUBIC_YARDS,
} as const;

type SiteUnitInput = Readonly<{
	distance?: string | null;
	area?: string | null;
	volume?: string | null;
}>;

const isDistanceKey = (k: string): k is keyof typeof DistanceUnitsMap =>
	k in DistanceUnitsMap;
const isAreaKey = (k: string): k is keyof typeof AreaUnitsMap =>
	k in AreaUnitsMap;
const isVolumeKey = (k: string): k is keyof typeof VolumeUnitsMap =>
	k in VolumeUnitsMap;

/**
 * Normalizes the Landlog Platform API unit object into SiteUnitSettings used internally.
 * Undefined or unknown values fall back to DEFAULT_SITE_UNITS.
 */
export const normalizeSiteUnitSettings = (
	units?: SiteUnitInput,
): SiteUnitSettings => ({
	distance:
		units?.distance && isDistanceKey(units.distance)
			? DistanceUnitsMap[units.distance]
			: DEFAULT_SITE_UNITS.distance,
	area:
		units?.area && isAreaKey(units.area)
			? AreaUnitsMap[units.area]
			: DEFAULT_SITE_UNITS.area,
	volume:
		units?.volume && isVolumeKey(units.volume)
			? VolumeUnitsMap[units.volume]
			: DEFAULT_SITE_UNITS.volume,
});
